import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { useCreateExportSalesTrackerContract, SalesExportTrackerContract } from "@/lib/api/exportSalesTracking";
import { Plus, Trash2, AlertCircle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface CreateContractsDialogProps {
  trackerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

interface ContractEntry {
  id: string;
  contract_number: string;
  has_been_signed: boolean;
  status?: "pending" | "submitting" | "success" | "failed";
  error?: string;
  retryCount?: number;
}

export function CreateContractsDialog({ trackerId, open, onOpenChange, onSuccess }: CreateContractsDialogProps) {
  const { toast } = useToast();
  const createContract = useCreateExportSalesTrackerContract();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contracts, setContracts] = useState<ContractEntry[]>([
    { id: crypto.randomUUID(), contract_number: "", has_been_signed: false, status: "pending" }
  ]);

  const addContract = () => {
    setContracts(prev => [
      ...prev,
      { id: crypto.randomUUID(), contract_number: "", has_been_signed: false, status: "pending" }
    ]);
  };

  const removeContract = (id: string) => {
    setContracts(prev => prev.filter(c => c.id !== id));
  };

  const updateContract = (id: string, updates: Partial<ContractEntry>) => {
    setContracts(prev => prev.map(c => 
      c.id === id ? { ...c, ...updates, status: "pending", error: undefined } : c
    ));
  };

  const resetDialog = () => {
    setContracts([{ id: crypto.randomUUID(), contract_number: "", has_been_signed: false, status: "pending" }]);
    setIsSubmitting(false);
  };

  const submitContract = async (contract: ContractEntry): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await createContract(trackerId, {
        contract_number: contract.contract_number,
        has_been_signed: contract.has_been_signed
      });

      if (result.success) {
        return { success: true };
      } else {
        return { success: false, error: result.message || "Failed to create contract" };
      }
    } catch (error) {
      return { success: false, error: "Network error occurred" };
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) { return; }
    // Validate all contracts have numbers
    const invalidContracts = contracts.filter(c => !c.contract_number.trim());
    if (invalidContracts.length > 0) {
      toast({
        title: "Validation Error",
        description: "All contracts must have a contract number",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    // Update all contracts to submitting status
    setContracts(prev => prev.map(c => ({ ...c, status: "submitting", error: undefined, retryCount: 0 })));

    const contractsToProcess = [...contracts];
    const maxRetries = 2;

    // Process each contract
    for (const contract of contractsToProcess) {
      let success = false;
      let lastError = "";
      
      // Try up to maxRetries + 1 times (initial + retries)
      for (let attempt = 0; attempt <= maxRetries && !success; attempt++) {
        const result = await submitContract(contract);
        success = result.success;
        lastError = result.error || "";

        if (!success && attempt < maxRetries) {
          // Update retry count in UI
          setContracts(prev => prev.map(c => 
            c.id === contract.id ? { ...c, retryCount: attempt + 1 } : c
          ));
          // Wait a bit before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      // Update final status
      setContracts(prev => prev.map(c => 
        c.id === contract.id 
          ? { ...c, status: success ? "success" : "failed", error: success ? undefined : lastError }
          : c
      ));
    }

    setIsSubmitting(false);

    // Check results - need to get the final state after all updates
    setContracts(prev => {
      const successCount = prev.filter(c => c.status === "success").length;
      const failedContracts = prev.filter(c => c.status === "failed");

      if (successCount === prev.length) {
        // All succeeded
        toast({
          title: "Success",
          description: `All ${successCount} contracts created successfully`
        });
        onSuccess?.();
        onOpenChange(false);
        resetDialog();
      } else if (successCount > 0) {
        // Partial success - will be handled in next render
        toast({
          title: "Partial Success",
          description: `${successCount} contracts created. ${failedContracts.length} failed. Please review and retry.`,
          variant: "default"
        });
      } else {
        // All failed
        toast({
          title: "Failed",
          description: "Failed to create any contracts. Please review errors and try again.",
          variant: "destructive"
        });
      }

      // Return filtered list if partial success
      if (successCount > 0 && successCount < prev.length) {
        return prev.filter(c => c.status === "failed");
      }
      
      return prev;
    });
  };

  return (
    <Dialog open={open} onOpenChange={(open) => {
      if (!open && !isSubmitting) {
        onOpenChange(false);
        resetDialog();
      }
    }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Create Contracts</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-4">
            {contracts.map((contract, index) => (
              <div key={contract.id} className={cn(
                "p-4 border rounded-lg space-y-3",
                contract.status === "success" && "border-green-500 bg-green-50",
                contract.status === "failed" && "border-red-500 bg-red-50"
              )}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-3">
                    <div>
                      <Label htmlFor={`contract-${contract.id}`}>
                        Contract Number {index + 1}
                        {contract.status === "submitting" && contract.retryCount && contract.retryCount > 0 && (
                          <span className="text-sm text-muted-foreground ml-2">
                            (Retry {contract.retryCount}/{2})
                          </span>
                        )}
                      </Label>
                      <Input
                        id={`contract-${contract.id}`}
                        value={contract.contract_number}
                        onChange={(e) => updateContract(contract.id, { contract_number: e.target.value })}
                        placeholder="Enter contract number"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`signed-${contract.id}`}
                        checked={contract.has_been_signed}
                        onCheckedChange={(checked) => 
                          updateContract(contract.id, { has_been_signed: !!checked })
                        }
                        disabled={isSubmitting}
                      />
                      <Label htmlFor={`signed-${contract.id}`} className="cursor-pointer">
                        Has been signed
                      </Label>
                    </div>

                    {contract.error && (
                      <div className="flex items-center gap-2 text-sm text-red-600">
                        <AlertCircle className="h-4 w-4" />
                        {contract.error}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {contract.status === "success" && (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    )}
                    {contract.status === "failed" && (
                      <AlertCircle className="h-5 w-5 text-red-600" />
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeContract(contract.id)}
                      disabled={isSubmitting || contracts.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            className="mt-4 w-full"
            onClick={addContract}
            disabled={isSubmitting}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Another Contract
          </Button>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              resetDialog();
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : `Create ${contracts.length} Contract${contracts.length > 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
